import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { ButtonSubmit, Form } from '@anytag/shared-atoms';
import {
  useAllAvailableCountryOptions,
  useAllAgenciesForSearchOptions,
  useAllAdvertisersForSearchOptions
} from '@anytag/shared-hooks';
import {
  TextField,
  SelectorField,
  PasswordField,
  CheckBoxField,
  AsyncSearchNetsuiteVendorField,
  AsyncSearchPrivateInfluencersField,
  SingleAsyncSearchNetsuiteVendorFieldProps
} from '@anytag/shared-molecules';
import { TalentAgencyFormKeys, TalentAgencyFormValues } from './types';

export const Template = () => {
  const { t } = useTranslation();
  const { allAgencyOptions } = useAllAgenciesForSearchOptions();
  const { allAdvertiserOptions } = useAllAdvertisersForSearchOptions();
  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const { isAdmin, isStaff, isAdminStaffTalentAgencyPartner } = useUserRoles();
  const {
    reset,
    watch,
    setValue,
    formState: { isSubmitting }
  } = useFormContext<TalentAgencyFormValues>();

  const values = watch();
  const isAddMode = values.type === 'add';

  const handleClickSendEmail = (_: string, value: boolean) => {
    // we need to clear password fields each time user click send email to prevent validation err on password
    reset({
      ...values,
      ...(isAddMode && { hasInvitationEmail: value, password: '', passwordConfirm: '' })
    });
  };

  const handleNetsuiteIdChange: SingleAsyncSearchNetsuiteVendorFieldProps<TalentAgencyFormKeys>['onChange'] = (
    name,
    value,
    options
  ) => {
    const option = options?.find((option) => option.value === value);

    setValue(name, value, { shouldValidate: true });
    setValue('countryId', option?.countryId || '');
    setValue('companyUrl', option?.companyUrl || '');
    setValue('talentAgencyName', option?.label || '');
  };

  return (
    <>
      <Form.Body className="form-container">
        <Form.SectionTitle index={isAddMode ? 1 : undefined} label={t('Talent Agency Information')} />

        {isAdminStaffTalentAgencyPartner ? (
          <AsyncSearchNetsuiteVendorField<TalentAgencyFormKeys> name="netsuiteId" onChange={handleNetsuiteIdChange} />
        ) : null}

        <TextField<TalentAgencyFormKeys>
          name="talentAgencyName"
          help={t<string>('Tooltip.Talent Agency Name')}
          title={t('TextForm.Talent Agency Name')}
          placeholder={t<string>('TextForm.AdAsiaLtd')}
          required
        />

        <SelectorField<TalentAgencyFormKeys>
          title={t('Selector.Agency')}
          name="agencyIds"
          help={t<string>('Tooltip.User Agency')}
          options={allAgencyOptions}
          multiple
        />

        <SelectorField<TalentAgencyFormKeys>
          title={t('Selector.Advertiser')}
          name="advertiserIds"
          help={t<string>('Tooltip.User Advertiser')}
          options={allAdvertiserOptions}
          multiple
        />

        <TextField<TalentAgencyFormKeys>
          name="companyUrl"
          help={t<string>('Tooltip.Talent Agency URL')}
          placeholder="https://app.anytag.jp"
          title={t('TextForm.Talent Agency URL')}
        />

        <Form.Fields itemsPerRow={2}>
          <TextField<TalentAgencyFormKeys>
            name="phoneNumber"
            help={t<string>('Tooltip.Phone Number')}
            placeholder="+66020485707"
            title={t('TextForm.Phone Number')}
          />

          <SelectorField<TalentAgencyFormKeys>
            title={t('Selector.Country')}
            name="countryId"
            help={t<string>('Tooltip.Agency Country')}
            options={allAvailableCountryOptions}
            required
          />
        </Form.Fields>

        {isAddMode ? (
          <>
            <Form.SectionTitle index={2} className="mt-6" label={t('User Information')} />

            <TextField<TalentAgencyFormKeys>
              name="name"
              title={t('TextForm.Name')}
              placeholder={t<string>('NameExample')}
              required
            />

            <Form.Fields itemsPerRow={2}>
              <TextField<TalentAgencyFormKeys>
                name="email"
                placeholder="your@email.com"
                title={t('TextForm.Email Address')}
                required
              />
            </Form.Fields>

            {!values.hasInvitationEmail && (
              <Form.Fields itemsPerRow={2}>
                <PasswordField<TalentAgencyFormKeys>
                  name="password"
                  title={t('Password.Password')}
                  required
                  dynamicError
                />
                <PasswordField<TalentAgencyFormKeys>
                  name="passwordConfirm"
                  title={t('Password.Confirm Password')}
                  required
                  dynamicError
                />
              </Form.Fields>
            )}

            <CheckBoxField<TalentAgencyFormKeys>
              name="hasInvitationEmail"
              onChange={handleClickSendEmail}
              label={t('Send invitation email')}
            />
          </>
        ) : values.type === 'edit' ? (
          <>
            <AsyncSearchPrivateInfluencersField<TalentAgencyFormKeys>
              agencyType="talent"
              disabled={!isAdmin}
              name="privateInfluencersIds"
              agencyId={Number(values.id)}
              placeholder={t<string>('Influencer name')}
              multiple
            />

            <Form.Fields itemsPerRow={2}>
              <TextField<TalentAgencyFormKeys>
                rightSection="%"
                placeholder="10"
                name="cmsMarginRate"
                title={t('TextForm.CG BGM Margin Rate')}
                help={t('Tooltip.Talent Agency Rate Help Text')}
              />
            </Form.Fields>
          </>
        ) : null}
      </Form.Body>

      <Form.Footer>
        <ButtonSubmit disabled={isStaff} type="submit" loading={isSubmitting} title={t('Button.Save')} />
      </Form.Footer>
    </>
  );
};
